/**
 * @file SectionLabel.tsx
 * @description Подпись секции капсом — единственная обёртка над
 * `.label-editorial` (phase19.24, UI-контракт §0.2).
 *
 * До контракта капс собирали руками: `text-xs font-bold uppercase
 * tracking-wider` в 15 вариантах — отсюда «шрифты меняются на каждой
 * странице». Теперь рецепт один: 12 / 600 / 0.08em / on-surface-variant.
 *
 * ```tsx
 * <SectionLabel as="h2" count={items.length} trailing={<Button>Добавить</Button>}>
 *   Позиции
 * </SectionLabel>
 * // → ПОЗИЦИИ (3)            [ Добавить ]
 * ```
 *
 * Цвет — только через `tone`: `.label-editorial` лежит в слое utilities после
 * классов Tailwind, поэтому `className="text-primary"` он молча перебивает
 * (находка ревью phase19.24 этап 0). `className` остаётся раскладке.
 */

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/** Тона подписи: обычная, акцентная (активная форма), приглушённая. */
export type SectionLabelTone = "default" | "primary" | "muted";

/** Реестр тонов: новый тон = запись здесь + класс в `index.css`, не ветка. */
const TONE_CLASS: Record<SectionLabelTone, string> = {
  default: "",
  primary: "label-editorial-primary",
  muted: "label-editorial-muted",
};

interface Props {
  /** Текст подписи: «Позиции», «Склад · Приёмка», «Принял». */
  children: ReactNode;
  /** Счётчик в скобках: «ПОЗИЦИИ (3)». `undefined` — скобок нет. */
  count?: number;
  /** Действие секции справа: кнопка, «Показано 3 из 3», переключатель. */
  trailing?: ReactNode;
  /**
   * Тег подписи. `h2` — когда подпись открывает секцию страницы (нужна
   * скринридеру для навигации), `dt` — подпись плитки внутри `<dl>`,
   * `div` (по умолчанию) — декоративная строка вроде хлебной крошки.
   */
  as?: "h2" | "div" | "dt";
  /** Цвет подписи. Через `className` цвет не задаётся — его перебьёт класс. */
  tone?: SectionLabelTone;
  /** Только раскладка и видимость: `gap-3`, `hidden lg:flex`. */
  className?: string;
}

export function SectionLabel({
  children,
  count,
  trailing,
  as = "div",
  tone = "default",
  className,
}: Props) {
  const Tag = as;
  const label = (
    <Tag
      className={cn(
        "label-editorial flex min-w-0 items-center gap-2",
        TONE_CLASS[tone],
        className,
      )}
    >
      {children}
      {count === undefined ? null : <span className="tabular-nums">({count})</span>}
    </Tag>
  );

  if (!trailing) return label;

  // Подпись слева, действие справа — на 360px действие переносится вниз,
  // а не выдавливает счётчик за край.
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      {label}
      <div className="flex shrink-0 items-center gap-2">{trailing}</div>
    </div>
  );
}
